import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NEVER, Observable, of } from 'rxjs';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { BinderClassification, BinderExportFiles } from 'src/app/models/binder/binder-classification.model';
import { BinderContent, BinderTabDocument } from 'src/app/models/binder/binder-content.model';
import { BinderDetails } from 'src/app/models/binder/binder-details.model';
import { BinderEditForm } from 'src/app/models/binder/binder-edit-form.model';
import { BinderEditJob } from 'src/app/models/binder/binder-edit-job.model';
import { BinderInfo } from 'src/app/models/binder/binder-info.model';
import { BinderQRScanHistory } from 'src/app/models/binder/binder-QR-scan-history.model';
import { BinderSetupData } from 'src/app/models/binder/binder-setup-data.model';
import { BinderUserAccessAccount } from 'src/app/models/binder/binder-user-access-account.model';
import { BinderUserPermission } from 'src/app/models/binder/binder-user-permission.model';
import { BinderWizardDetails } from 'src/app/models/binder/binder-wizard-details.model';
import { JobLogbook } from 'src/app/models/my-work/job-logbook.model';
import { environment } from 'src/environments/environment';
import { BinderFilter} from '../../../models/binder/binder-filter.model';
import { BinderDocumentType} from '../../../models/binder/binder-documentType-model';
import { ReadingMeasurement } from 'src/app/models/binder/binder-reading-measurement.model';
import { NormalizationFieldExceptionCriteria } from 'src/app/models/normalization-field-exception-criteria.model';
import { BaseBinderWizardService } from '../../interfaces/base-binder-wizard-service';
import {BinderSessionConfirmationResponse, BinderSessionStatus} from '../../../models/binder/binder-session-confirmation-response.model';

@Injectable({
	providedIn: 'root'
})
export class BinderSessionWizardService implements BaseBinderWizardService {

	private baseUrl = `${environment.baseUrl}/binder-session`;

	constructor(private httpClient: HttpClient) {}

	@ApiMethod()
	getBinderWizardDetails(token: string): Observable<BinderWizardDetails> {
		return NEVER;
	}

	@ApiMethod()
	submitBinderQuery(token: string, data: {email: string, hasWorkToDo: boolean, userAccountId?: number, isChannelPartner: boolean}): Observable<void> {
		return NEVER;
	}

	@ApiMethod()
	submitCode(token: string, code: string): Observable<{ token: string }> {
		return NEVER;
	}

	@ApiMethod()
	getBinderDetails(token: string): Observable<BinderDetails> {
		return this.httpClient.get<BinderDetails>(`${this.baseUrl}/${token}/viewer`);
	}

	@ApiMethod()
	getBinderYears(token: string): Observable<BinderSetupData> {
		return this.httpClient.get<BinderSetupData>(`${this.baseUrl}/${token}/viewer/years`);
	}

	@ApiMethod()
	getBinderClassificationDocuments(token: string, binderFilter: BinderFilter): Observable<BinderContent> {
		let params = new HttpParams()
			.set('year', binderFilter.year ? binderFilter.year.toString() : '')
			.set('fromDate', binderFilter.fromDate ? binderFilter.fromDate.toISOString() : '')
			.set('toDate', binderFilter.toDate ? binderFilter.toDate.toISOString() : '')
			.set('showReviewRequired', binderFilter.hideReviewRequired ? 'false' : 'true')
			.set('serviceCategoryDocumentReadingId', binderFilter.serviceCategoryDocumentReadingId != null
				? binderFilter.serviceCategoryDocumentReadingId.toString()
				: '')
			.set('readingCriteria', binderFilter.readingCriteria || '')
			.set('includeExpectationTags', binderFilter.includeExpectationTags ? 'true' : 'false')
			.set('expectationAssetTagId', binderFilter.expectationAssetTagId ? binderFilter.expectationAssetTagId.toString() : '');

		if (Array.isArray(binderFilter.readingValue) && binderFilter.readingValue.length > 0) {
			const readingValueStr = JSON.stringify(binderFilter.readingValue);
			params = params.set('readingValue', readingValueStr);
		}
		else if (typeof binderFilter.readingValue === 'string' && binderFilter.readingValue.trim() !== '') {
			params = params.set('readingValue', binderFilter.readingValue);
		}
		else {
			params = params.set('readingValue', '');
		}

		if (binderFilter.documentTypeIds?.length > 0) {
			binderFilter.documentTypeIds.forEach(id => {
				params = params.append('documentTypeIds', id.toString());
			});
		}

		return this.httpClient.get<BinderContent>(`${this.baseUrl}/${token}/viewer/documents`, { params });
	}

	@ApiMethod()
	getBinderDocumentTypes(token: string): Observable<BinderDocumentType[]>{
		return of([]);
	}

	@ApiMethod()
	getBinderReadingsMeasurements(token: string, documentTypeIds: number[], commonReadingsOnly: boolean): Observable<ReadingMeasurement[]> {
		return NEVER;
	}

	@ApiMethod()
	getNormalizationFieldExceptionCriterias(): Observable<NormalizationFieldExceptionCriteria[]>{
		return NEVER;
	}

	@ApiMethod()
	getJobDocument(token: string, documentId: number): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/${token}/viewer/documents/${documentId}`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	trackBinderSessionDocumentView(req: { token: string; urlHashActionTypeId: number; documentId: number; parentTabId?: number; childTabId?: number; }): Observable<void> {

		if (!req.documentId) {
			throw new Error('documentId must be provided and greater than 0.');
		}

		let params = new HttpParams()
			.set('urlHashActionTypeId', req.urlHashActionTypeId.toString());

		if (req.parentTabId != null) params = params.set('parentTabId', req.parentTabId.toString());
		if (req.childTabId != null) params = params.set('childTabId', req.childTabId.toString());

		return this.httpClient.post<void>(
			`${this.baseUrl}/${req.token}/viewer/documents/${req.documentId}/view`,
			null,
			{ params });
	}

	@ApiMethod()
	getBinderClassification(token: string): Observable<BinderClassification[]> {
		return this.httpClient.get<BinderClassification[]>(`${this.baseUrl}/${token}/viewer/classification`);
	}

	@ApiMethod()
	hasVendorBinderAccess(token: string): Observable<boolean> {
		return of(false);
	}

	@ApiMethod()
	hasAddToBinderAccess(token: string): Observable<boolean> {
		return of(false);
	}

	@ApiMethod()
	getVirtualBinderDigitalLogbook(token: string): Observable<JobLogbook[]> {
		return NEVER;
	}

	@ApiMethod()
	getVirtualBinderInfo(token: string, digitalLogFormId: number, jobId: number): Observable<BinderInfo> {
		return NEVER;
	}

	@ApiMethod()
	getVirtualBinderAccessHash(token: string, digitalLogFormId: number): Observable<{binderUri?: string}> {
		return NEVER;
	}

	@ApiMethod()
	getBinderTargerEmailAddress(token: string): Observable<{targetEmail: string}> {
		return NEVER;
	}

	@ApiMethod()
	submitDocumentation(token: string, formData: {binderClassificationId: number, documents: BinderTabDocument[], groupName: string}[], year: number): Observable<void> {
		return NEVER;
	}

	@ApiMethod()
	getQRScanHistory(token: string): Observable<BinderQRScanHistory[]> {
		return NEVER;
	}

	@ApiMethod()
	hasAccessToQRScansHistory(token: string): Observable<boolean> {
		return NEVER;
	}

	@ApiMethod()
	hasEditBinderAccess(token: string): Observable<boolean> {
		return of(false);
	}

	@ApiMethod()
	hasWorkToDo(token: string): Observable<JobLogbook> {
		return NEVER;
	}

	@ApiMethod()
	hasCustomerPermission(token: string, permissionType: number): Observable<boolean> {
		return of(false);
	}

	@ApiMethod()
	getBinderUserAccess(token: string): Observable<BinderUserPermission> {
		return of({ hasAddToBinderAccess: false });
	}

	@ApiMethod()
	hasBinderUserPermission(token: string): Observable<boolean> {
		return NEVER;
	}

	@ApiMethod()
	getBinderEditForms(token: string, binderTabId: number): Observable<BinderEditForm[]> {
		return NEVER;
	}

	@ApiMethod()
	getBinderEditJobs(token: string, formTemplateId: number, year?: number): Observable<BinderEditJob[]> {
		return NEVER;
	}

	@ApiMethod()
	getBinderUserAccessData(token: string, email: string): Observable<BinderUserAccessAccount> {
		return NEVER;
	}

	@ApiMethod()
	isBinderAndCustomerLegacy(token: string): Observable<boolean> {
		return NEVER;
	}

	@ApiMethod()
	isBinderEmailAllowed(token: string, email: string): Observable<boolean> {
		return NEVER;
	}

	@ApiMethod()
	checkAccessToken(hashId, email, token): Observable<boolean> {
		return NEVER;
	}

	@ApiMethod()
	getCountries(): Observable<any[]> {
		return NEVER;
	}

	@ApiMethod()
	getStates(): Observable<any[]> {
		return NEVER;
	}

	@ApiMethod()
	getBindersByAddress(addressId): Observable<any[]> {
		return NEVER;
	}

	@ApiMethod()
	getSiteAddresses(latitude, longitude): Observable<any[]> {
		return NEVER;
	}

	@ApiMethod()
	exportBinderFiles(token: string, binderContent: BinderExportFiles): Observable<void> {
		return NEVER;
	}

	@ApiMethod()
	confirmStatus(token: string): Observable<BinderSessionConfirmationResponse> {
		return this.httpClient.get<BinderSessionConfirmationResponse>(
			`${this.baseUrl}/${token}/confirm/status`);
	}

	@ApiMethod()
	confirmStatusRequest(token: string): Observable<BinderSessionConfirmationResponse> {
		return this.httpClient.get<BinderSessionConfirmationResponse>(`${this.baseUrl}/${token}/confirm/status/request`);
	}

	@ApiMethod()
	confirmCode(token: string, confirmationCode: string): Observable<void> {
		const params = new HttpParams().set('code', confirmationCode);
		return this.httpClient.get<void>(`${this.baseUrl}/${token}/confirm`, { params });
	}

	@ApiMethod()
	status(token: string): Observable<BinderSessionStatus>{
		return this.httpClient.get<BinderSessionStatus>(`${this.baseUrl}/${token}/status`);
	}
}
